import type { MembershipDto, UserDto } from '@aawas/contracts';
import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { getActive, getSession, setActive, subscribeSession } from './api';

interface SessionValue {
  user: UserDto;
  memberships: MembershipDto[];
  /** The membership the app is currently acting as (drives the dashboard). */
  current: MembershipDto;
  switchTo: (m: MembershipDto) => void;
}

const Ctx = createContext<SessionValue | null>(null);

/** Re-renders on any session/active-context change. Returns null when signed out. */
export function useRawSession() {
  const s = useSyncExternalStore(subscribeSession, getSession);
  const a = useSyncExternalStore(subscribeSession, getActive);
  return { session: s, active: a };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { session, active } = useRawSession();
  const value = useMemo<SessionValue | null>(() => {
    if (!session) return null;
    const current = session.memberships.find((m) => m.communityId === active?.communityId && m.role === active?.role) ?? session.memberships[0];
    if (!current) return null; // signed in but not a member of anything
    return {
      user: session.user,
      memberships: session.memberships,
      current,
      switchTo: (m) => setActive({ communityId: m.communityId, role: m.role }),
    };
  }, [session, active]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Only call inside authenticated routes. */
export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside an authenticated route');
  return v;
}
export const useMaybeSession = () => useContext(Ctx);

export const ROLE_LABEL: Record<MembershipDto['role'], string> = {
  admin: 'Committee',
  owner: 'Owner',
  tenant: 'Tenant',
  family: 'Family',
  guard: 'Security',
  staff: 'Facility staff',
};
export const isResident = (r: MembershipDto['role']) => r === 'owner' || r === 'tenant' || r === 'family';
